/**
 * The compile pipeline itself: source in, drawable graph out.
 *
 * Shared verbatim between the worker and the synchronous fallback, so the two
 * can never produce different diagrams.
 */
import { analyzeSource } from '../language';
import { hasErrors } from '../language/diagnostics/types';
import { buildDiagram } from '../graph';
import type { PipelineOptions, PipelineRequest, PipelineResult } from './pipelineTypes';

export function runPipeline(request: PipelineRequest): PipelineResult {
  const started = now();
  const { source, options } = request;

  const analysis = analyzeSource(source);
  const { graph, nodeRanges } = buildDiagram(analysis.program, analysis.semantics, {
    compile: compileOptionsFrom(options),
    layout: {
      direction: options.direction,
      nodeSpacing: options.nodeSpacing,
      rankSpacing: options.rankSpacing,
    },
  });

  return {
    id: request.id,
    source,
    diagnostics: analysis.diagnostics,
    graph,
    nodeRanges: [...nodeRanges.entries()],
    functionNames: [...analysis.semantics.functions.values()].map((fn) => fn.name.text),
    hasErrors: hasErrors(analysis.diagnostics),
    durationMs: Math.round(now() - started),
  };
}

function compileOptionsFrom(options: PipelineOptions) {
  return {
    direction: options.direction,
    functionDisplay: options.functionDisplay,
    functionOverrides: new Map(options.functionOverrides),
    yesLabel: options.yesLabel,
    noLabel: options.noLabel,
    terminatorLabelStyle: options.terminatorLabelStyle,
    showUnusedFunctions: options.showUnusedFunctions,
  };
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}
